import { auth, db } from '@/api/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneNumberInput from '@/components/ui/phone-number-input';
import { useToast } from '@/components/ui/use-toast';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

function CreateAccount() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      const user = userCredential.user;

      await Promise.all([
        setDoc(doc(db, 'users', user.uid), {
          role: 'admin',
          schoolName: data.schoolName,
          email: data.email,
          phone: data.phone,
          password: data.password,
          isAccountDisabled: false,
          defaultBranchId: 'default',
          createdAt: serverTimestamp(),
        }),
        setDoc(doc(db, `users/${user.uid}/branches/default`), {
          branchId: 'default',
          name: data.schoolName,
          address: data.address || '',
          createdAt: serverTimestamp(),
        }),
        await setDoc(doc(db, `leads/${user.uid}`), {
          leadId: 'default',
          userId: user.uid,
          name: 'Asosiy Lead',
          createdAt: serverTimestamp(),
          columns: [
            {
              id: 'new',
              title: 'New Leads',
            },
          ],
        }),
      ]);

      toast({ title: 'Account created successfully!' });
      reset();
      navigate('/');
    } catch (error) {
      console.error(error);
      toast({ title: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full xl:grid xl:grid-cols-3 h-screen overflow-hidden">
      <div className="relative flex items-center justify-center h-screen xl:h-auto xl:py-12">
        {/* Logo */}
        <div className="absolute top-4 lg:top-8 mx-auto grid w-[350px] cursor-pointer hover:opacity-90 transition-opacity duration-300">
          <img
            className="w-20 lg:w-28 block dark:hidden"
            src="/assets/logos/studypro-ceo-black.svg"
            alt="Light mode logo"
          />
          <img
            className="w-20 lg:w-28 hidden dark:block"
            src="/assets/logos/studypro-ceo-white.svg"
            alt="Dark mode logo"
          />
        </div>

        {/* Form */}
        <div className="mx-auto grid w-[350px] gap-6">
          <div className="grid gap-2 text-center">
            <h1 className="text-2xl font-bold">Create Account</h1>
            <p className="text-sm text-muted-foreground">
              Enter your details to register in StudyPro
            </p>
          </div>

          <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Full Name */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label required htmlFor="schoolName">
                  O'quv markaz nomi
                </Label>
                {errors.schoolName && (
                  <span className="text-red-500 text-xs">
                    {errors.schoolName.message}
                  </span>
                )}
              </div>
              <Input
                id="schoolName"
                placeholder="StudyPro Academy"
                {...register('schoolName', {
                  required: 'Full name is required',
                })}
              />
            </div>

            {/* Email */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label required htmlFor="email">
                  Email
                </Label>
                {errors.email && (
                  <span className="text-red-500 text-xs">
                    {errors.email.message}
                  </span>
                )}
              </div>
              <Input
                id="email"
                type="email"
                placeholder="email@gmail.com"
                {...register('email', { required: 'Email is required' })}
              />
            </div>

            {/* Phone */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label required htmlFor="phone">
                  Phone Number
                </Label>
                {errors.phone && (
                  <span className="text-red-500 text-xs">
                    {errors.phone.message}
                  </span>
                )}
              </div>
              <Controller
                name="phone"
                control={control}
                defaultValue="+998"
                rules={{
                  required: "Bu yerni to'ldirish talab qilinadi",
                  minLength: {
                    value: 10,
                    message: "Yetarlicha raqam qo'shilmadi",
                  },
                  maxLength: { value: 13, message: 'Raqam juda uzun' },
                }}
                render={({ field }) => (
                  <PhoneNumberInput
                    id="phone"
                    disabled={isSubmitting}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Password */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label required htmlFor="password">
                  Password
                </Label>
                {errors.password && (
                  <span className="text-red-500 text-xs">
                    {errors.password.message}
                  </span>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label required htmlFor="confirmPassword">
                  Confirm Password
                </Label>
                {errors.confirmPassword && (
                  <span className="text-red-500 text-xs">
                    {errors.confirmPassword.message}
                  </span>
                )}
              </div>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="********"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === watch('password') || 'Passwords do not match',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button className="w-full" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Account'}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            Allaqachon hisob bormi?{' '}
            <Link to="/login" className="underline">
              Kirish
            </Link>
          </div>
        </div>
      </div>

      {/* Right side image */}
      <div className="hidden bg-muted xl:block col-span-2">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/crm-system-4fefe.appspot.com/o/cover%2Fcrm-cover.png?alt=media&token=611e8787-6118-454e-aa02-1372d844fe7e"
          alt="Image"
          className="h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}

export default CreateAccount;
